// WHAT EACH ROUTE PUTS IN THE DOCUMENT HEAD.
//
// There is no router and no server rendering here: every route is a view swap through pushState,
// so the title, the description and the canonical link all have to be set from the view that is
// on screen and restored when it leaves. Before this, only the methodology and Questions pages
// set a title, neither set a description, and nothing anywhere set a canonical link - while
// render.yaml serves the same build from both dealquire.in and www.dealquire.in and rewrites
// every path to index.html, so two hostnames and any number of paths answered with identical
// metadata.
//
// ONE PLACE, because three routes setting their own head tags three different ways is how they
// drift. Each route calls useRouteHead once with its own three values.
import { useEffect } from 'react';

export const SITE_ORIGIN = 'https://dealquire.in';

function setMeta(selector, attr, value, create) {
  let el = document.head.querySelector(selector);
  let made = false;
  if (!el) {
    el = create();
    document.head.appendChild(el);
    made = true;
  }
  const prev = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return () => {
    // A tag this hook created is removed again; one that was already in the HTML keeps the
    // value it shipped with, so leaving a route cannot strip the page's own metadata.
    if (made) el.remove();
    else if (prev !== null) el.setAttribute(attr, prev);
  };
}

export function useRouteHead({ title, description, path }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;
    const undo = [];
    if (description) {
      undo.push(setMeta('meta[name="description"]', 'content', description, () => {
        const m = document.createElement('meta');
        m.setAttribute('name', 'description');
        return m;
      }));
    }
    if (path) {
      undo.push(setMeta('link[rel="canonical"]', 'href', SITE_ORIGIN + path, () => {
        const l = document.createElement('link');
        l.setAttribute('rel', 'canonical');
        return l;
      }));
    }
    return () => {
      undo.forEach(fn => fn());
      document.title = prevTitle;
    };
  }, [title, description, path]);
}
